import { Link } from "react-router-dom";
import { athletes as seedAthletes, type Athlete, type Brand } from "@/data/athletes";
import { useSiteContent } from "@/hooks/useSiteContent";

type Item = Brand & { athleteNames: string[] };

const ShopSection = ({ athletes = seedAthletes }: { athletes?: Athlete[] }) => {
  const { t } = useSiteContent();

  // Dedupe products by id (or brand+product fallback for seeds).
  const map = new Map<string, Item>();
  athletes.forEach((a) => {
    a.brands.forEach((b) => {
      const key = b.id ?? `${b.name}::${b.product}`;
      const linked = b.athletes?.map((x) => x.name) ?? [a.name];
      const existing = map.get(key);
      if (existing) {
        linked.forEach((n) => { if (!existing.athleteNames.includes(n)) existing.athleteNames.push(n); });
      } else {
        map.set(key, { ...b, athleteNames: [...linked] });
      }
    });
  });
  const items = Array.from(map.values());

  if (items.length === 0) return null;

  const formatVia = (names: string[]) => {
    if (names.length === 0) return "";
    const firsts = names.map((n) => n.split(" ")[0]);
    if (firsts.length === 1) return `via ${firsts[0]}`;
    if (firsts.length === 2) return `via ${firsts[0]} & ${firsts[1]}`;
    return `via ${firsts[0]} + ${firsts.length - 1} others`;
  };

  return (
    <section id="shop" className="py-24 bg-secondary text-secondary-foreground">
      <div className="container">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">{t("shop.eyebrow", "Shop her picks")}</span>
            <h2 className="text-5xl md:text-7xl mt-3 text-balance">{t("shop.heading", "Wear what they wear.")}</h2>
          </div>
          <p className="opacity-70 max-w-sm">
            {t("shop.intro", "Discover brands these athletes partner with and support.")}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map((item, i) => {
            const galleryCount = item.galleryUrls?.length ?? 0;
            const Wrapper: any = item.id ? Link : "div";
            const wrapperProps = item.id ? { to: `/product/${item.id}` } : {};
            return (
              <Wrapper
                key={`${item.id ?? item.product}-${i}`}
                {...wrapperProps}
                className="group block bg-background text-foreground rounded-2xl p-5 hover:bg-accent hover:text-accent-foreground transition-all duration-300 cursor-pointer"
              >
                <div className="relative aspect-square rounded-xl bg-muted group-hover:bg-background/30 mb-4 overflow-hidden flex items-center justify-center font-display text-4xl text-primary group-hover:text-background transition-colors">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.product} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <span>{item.name.charAt(0)}</span>
                  )}
                  {item.isHouseBrand && (
                    <span className="absolute top-2 left-2 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                      Official
                    </span>
                  )}
                  {galleryCount > 0 && (
                    <span className="absolute bottom-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-background/80 text-foreground">
                      +{galleryCount}
                    </span>
                  )}
                </div>
                <p className="text-[10px] uppercase tracking-wider opacity-60">{item.name} · {item.category}</p>
                <p className="font-semibold mt-1 text-sm leading-tight">{item.product}</p>
                {item.quote && (
                  <p className="serif italic text-xs opacity-80 mt-2 line-clamp-2">“{item.quote}”</p>
                )}
                <div className="flex justify-between items-baseline mt-3">
                  <span className="font-display text-lg">{item.price}</span>
                  <span className="text-xs opacity-70">{formatVia(item.athleteNames)}</span>
                </div>
                {item.id && (
                  <span className="block mt-3 text-xs font-semibold uppercase tracking-wider opacity-70 group-hover:opacity-100">
                    View details →
                  </span>
                )}
              </Wrapper>
            );
          })}
          
        </div>
      </div>
    </section>
  );
};

export default ShopSection;
